import Honeybadger from '@honeybadger-io/js';
import { Noticeable, Notice } from '@honeybadger-io/core/build/src/types';
import pino from 'pino';

import { getNextConfigValues } from '../next-config/index';
import { isServerSide } from '../src/utils/next';

export interface LogPayload {
  msg: string;
  noticable?: Noticeable;
  extra?: Partial<Notice>;
  params?: Record<any, any>;
}

type LogFunction = (payload: LogPayload) => void;

interface Logger {
  debug: LogFunction;
  info: LogFunction;
  warn: LogFunction;
  error: LogFunction;
}

const pinoLogger = pino();

/**
 * Whether or not pino logger should be called.
 *
 * Should evaluate to true in all situations except for when we're in prod on client side.
 *
 * Tried a `logToPino` helper function where we could pass the log level in as a param and just pluck the appropriate function off of the
 * pino instance to log; something like
 *
 * `function logToPino(pinoInstance, logLevel) {
 *    if (shouldLogPino()) {
 *      const logFn = pinoInstance[logLevel]
 *    }
 * }`
 *
 * felt it'd be a little more DRY and allow us to have this check centrally located
 * rather than having to check in every log case, but it wasn't working as per this issue: https://github.com/pinojs/pino/issues/569
 *
 * Seemed just as non-DRY to have to bind on every call but idk might still adjust a little bit later.
 */
export const shouldLogPino = (): boolean =>
  isServerSide() ||
  getNextConfigValues().publicRuntimeConfig.HONEYBADGER_ENV !== 'prod';

/**
 * General purpose logger for frontend. Leverages pino to log on both server and client side; also
 * sends error logs to HoneyBadger.
 *
 * NOTE: only `logger.error` will send a notification to HoneyBadger; all other log functions will
 * simply log to pino.
 *
 * Usage:
 *
 * ```
 * import logger from 'logger';
 *
 * ...
 *
 * logger.error({
 *   msg: 'Failed to fetch meal',
 *   noticable: err
 * })
 * ```
 */
const logger: Logger = {
  debug: (args) => {
    if (shouldLogPino()) {
      pinoLogger.debug(args);
    }
  },

  info: (args) => {
    if (shouldLogPino()) {
      pinoLogger.info(args);
    }
  },

  warn: (args) => {
    if (shouldLogPino()) {
      pinoLogger.warn(args);
    }
  },

  /**
   * Log error; will also send notification to HoneyBadger if configured.
   */
  error: (args) => {
    if (shouldLogPino()) {
      pinoLogger.error(args);
    }
    const { msg, noticable, extra } = args;
    if (noticable) {
      Honeybadger.notify(noticable, msg, extra);
    } else {
      Honeybadger.notify(msg, extra);
    }
  },
};

export default logger;
